import { TFunction } from 'i18next';
import React, { MouseEvent, ReactNode } from 'react';
import { DISPLAY_TYPE, Views } from '../BlockInteractions.utils';
import { Icons, SVGIcon } from '../../../atoms/SVG';
import Btn from '../../../molecules/Btn';
import {
  ContentWrapperActionType,
  ContentWrapperBlockTypes,
} from '../../../blocks/ContentWrapper/ContentWrapper.types';
import IForm from '../../../../types/IForm';
import { TooltipPlacement } from '../../../atoms/Tooltip';
import { InteractionButton } from './InteractionButton';
import {
  BlockInteractionsState,
  ButtonsType,
  GetButtonModifier,
  GetHref,
  GetTitleText,
  ToggleActiveView,
  ViewsType,
} from '../BlockInteractionsTypes';
import { ContentWrapperContext } from '../../../blocks/ContentWrapper/ContentWrapperContext';

type ViewAndActionButtonsType = {
  state: BlockInteractionsState;
  buttons?: ButtonsType;
  views: ViewsType;
  id: string;
  uuid: string;
  elementType: string;
  contentWrapperProps?: ContentWrapperContext | null;
  getTitleText: GetTitleText;
  getButtonModifier: GetButtonModifier;
  getHref: GetHref;
  isInLayoutGroup?: boolean;
  toggleActiveView: ToggleActiveView;
  t: TFunction;
};

export function ViewAndActionButtons({
  state,
  buttons,
  views,
  id,
  uuid,
  elementType,
  contentWrapperProps,
  getTitleText,
  getButtonModifier,
  getHref,
  isInLayoutGroup,
  toggleActiveView,
  t,
}: ViewAndActionButtonsType) {
  const { primaryView, secondaryView } = state;

  // NOTE that {} (defaultProp) evaluates to true
  // So this code is basically unreachable?!
  if (!buttons) {
    return null;
  }

  const viewButtons = Object.keys(buttons).map((viewName: string): ReactNode | null => {
    if (views[viewName as keyof typeof views] || viewName === 'action') {
      const { displayType, isDisabled, svgSpriteId, transNamespaceAndKey, action } =
        buttons[viewName as keyof typeof buttons];

      const viewIsActive = [primaryView, secondaryView].includes(viewName as any);
      const ariaControls =
        displayType === DISPLAY_TYPE.MODAL || viewName === Views.ACTION ? undefined : `block-content-${id}`;
      const ariaExpanded = displayType === DISPLAY_TYPE.MODAL || viewName === Views.ACTION ? undefined : viewIsActive;
      const onClick =
        action ||
        ((event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => toggleActiveView(event, viewName as any));
      const spriteId = viewIsActive ? Icons.DECLINE_CROSS : svgSpriteId;

      // in <TeamroomManagment /> we have text buttons without any svgSpriteId, like "Freigabe beantragen"
      if (!svgSpriteId) {
        return (
          <div key={`${viewName}-view-${uuid}`} className="block-interactions__button-container">
            <Btn
              cssClass="btn--single-action forms__button"
              text={t(`${transNamespaceAndKey}.text`)}
              // aria-controls and expanded is not needed when type is modal
              // it always controls the block content!
              ariaControls={ariaControls}
              ariaExpanded={ariaExpanded}
              ariaDisabled={isDisabled}
              onClick={onClick}
            />
          </div>
        );
      }

      return (
        <div key={`${viewName}-view-${uuid}`} className="block-interactions__button-container">
          <Btn
            text={getTitleText(contentWrapperProps, transNamespaceAndKey, elementType, 'text')}
            titleText={getTitleText(contentWrapperProps, transNamespaceAndKey, elementType, 'titleText')}
            hiddenText
            modifier={getButtonModifier(svgSpriteId as SVGIcon)}
            svgSpriteId={spriteId}
            href={isDisabled ? undefined : getHref(viewName)}
            // aria-controls and expanded is not needed when type is modal
            // it always controls the block content!
            ariaControls={ariaControls}
            ariaExpanded={ariaExpanded}
            ariaDisabled={isDisabled}
            ariaHaspopup={
              viewName === Views.DELETE || viewName === Views.DELETE_BLOCK || viewName === Views.CONNECT_BLOCKS
                ? true
                : undefined
            }
            routing={false}
            tooltipPlacement={TooltipPlacement.TOP}
            onClick={onClick}
          />
        </div>
      );
    }

    return null;
  });

  const actionButtons = contentWrapperProps
    ? Object.entries(contentWrapperProps?.actions || {}).map(([actionType, form]) => {
        return (
          <div key={`${actionType}-action-${uuid}`} className="block-interactions__button-container">
            {/* @ts-expect-error  TODO: deleteBlock doesn't match */}
            <InteractionButton
              uuid={uuid}
              elementType={elementType as ContentWrapperBlockTypes}
              actionType={actionType as ContentWrapperActionType}
              form={form as IForm}
              isInLayoutGroup={isInLayoutGroup || false}
              toggleActiveView={toggleActiveView}
              index={contentWrapperProps.index}
              nextBlockIsRichtext={contentWrapperProps.nextBlockIsRichtext}
              numBlocks={contentWrapperProps.numBlocks}
              onMovingFailure={contentWrapperProps.onMovingFailure}
              onMovingSuccess={contentWrapperProps.onMovingSuccess}
              setAction={contentWrapperProps.setAction}
              addBlock={contentWrapperProps.addBlock}
              setError={contentWrapperProps.setError}
            />
          </div>
        );
      })
    : [];
  return [...viewButtons, ...actionButtons];
}
